import { Button, Table, Wrapper, message, useBreadcrumbRoutes, FormContent, useUpdate } from '@maxtropy/components';
import { Space, TreeSelect } from 'antd';
import React, { useState, useEffect } from 'react';
import {
  apiV2AreaTreePost,
  apiV2PipelinePressureMonitoringAddMonitorConfigPost,
  apiV2PipelinePressureMonitoringGetAllMonitorConfigPost,
} from '@maxtropy/device-customer-apis-v2';
import { PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import styles from './index.module.scss';
import DeviceConfig from './DeviceConfig';
import DataPointConfig from './DataPointConfig';
import { useRequest } from 'ahooks';

// 监测区域数据类型
export interface MonitorAreaItem {
  id?: number | string;
  areaInfo: { id?: number; zoneName?: string }[]; // 区域信息数组，包含 id 和 zoneName
  devices: { id?: number; name?: string; code?: string }[]; // 设备对象数组
}

// 压力数据点类型
export interface PressureDataPoint {
  id: number;
  category: string; // 一级类目
  subCategory: string; // 二级类目
  attribute: string; // 数据属性
  dataType: string; // 数据类型（压力/流量等）
}

export interface OrganizationTreeProps {
  id?: number;
  level?: number;
  zoneName?: string;
  children?: OrganizationTreeProps[];
}

export interface TreeSelectOption {
  value: number;
  title: string;
  children?: TreeSelectOption[];
}
const transformToTreeSelectOptions = (nodes: OrganizationTreeProps[]): TreeSelectOption[] => {
  return nodes
    .filter(node => node.id != null && node.zoneName != null) // 过滤无效节点
    .map(node => ({
      value: node.id!,
      title: node.zoneName!,
      children: node.children ? transformToTreeSelectOptions(node.children) : undefined,
    }));
};

// 根据 id 查找节点
const findNodeById = (options: TreeSelectOption[], id: number): TreeSelectOption | undefined => {
  for (const node of options) {
    if (node.value === id) return node;
    if (node.children) {
      const found = findNodeById(node.children, id);
      if (found) return found;
    }
  }
  return undefined;
};

const PipelinePressureConfig: React.FC = () => {
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const navigate = useNavigate();
  const [option, setOption] = useState<any>([]);
  const [areaList, setAreaList] = useState<MonitorAreaItem[]>([]);
  const [isDataPointModalVisible, setIsDataPointModalVisible] = useState(false);
  const [isDeviceConfigModalVisible, setIsDeviceConfigModalVisible] = useState(false);
  const [currentAreaId, setCurrentAreaId] = useState<number | null>(null);
  const [deviceIds, setDeviceIds] = useState<number[]>();
  const [isEditMode, setIsEditMode] = useState(false);
  const [update, updateFn] = useUpdate();
  // 保存初始数据，用于取消时恢复
  const [initialData, setInitialData] = useState<MonitorAreaItem[]>([]);
  //获取监测配置列表
  const { data: tableData } = useRequest(
    async () => {
      const res = await apiV2PipelinePressureMonitoringGetAllMonitorConfigPost({});
      const list = res.list || [];
      return list.map(item => ({
        id: item.id,
        areaInfo: item.areaInfo || [],
        devices: item.devices || [],
      }));
    },
    { refreshDeps: [update] }
  );

  // 当 API 数据加载完成后，初始化 areaList 和 deviceIds
  useEffect(() => {
    if (tableData && tableData.length > 0) {
      setAreaList(tableData);
      setInitialData(tableData);
      // 提取所有区域的设备 ID
      const allDeviceIds = tableData.flatMap(item => item.devices?.filter(d => d.id).map(d => d.id!) || []);
      setDeviceIds(allDeviceIds);
    }
  }, [tableData, update]);

  // 新增监测区域
  const handleAddArea = () => {
    const newId = Date.now(); // 生成唯一临时 ID
    setAreaList([...areaList, { id: `${newId}`, areaInfo: [], devices: [] }]);
  };

  // 获取区域名称列表
  useEffect(() => {
    apiV2AreaTreePost({}).then(data => {
      const dataList = data.list || [];
      const options = transformToTreeSelectOptions(dataList);
      setOption(options);
    });
  }, []);

  // 删除监测区域
  const handleDeleteArea = (id: number) => {
    setAreaList(areaList.filter(item => item.id !== id));
  };

  // 配置压力设备
  const handleConfigPressureDevice = (id: number) => {
    setCurrentAreaId(id);
    setIsDeviceConfigModalVisible(true);
  };

  // 进入编辑模式
  const handleEdit = () => {
    setIsEditMode(true);
  };

  // 保存配置
  const handleSave = () => {
    // 过滤出有选择区域的配置项
    const validConfigs = areaList.filter(item => item.areaInfo && item.areaInfo.length > 0);

    if (validConfigs.length === 0) {
      message.error('请至少选择一个监测区域');
      return;
    }

    const monitoringConfigs = validConfigs.map(item => ({
      id: typeof item.id === 'number' ? item.id : undefined,
      areaInfo: item.areaInfo as { id?: number; zoneName?: string }[],
      devices: (item.devices || [])
        .filter(d => d.id)
        .map(d => ({
          id: d.id!,
          name: d.name || '',
          code: d.code || '',
        })),
    }));

    apiV2PipelinePressureMonitoringAddMonitorConfigPost({
      monitoringConfigs,
    })
      .then(() => {
        message.success('保存成功');
        updateFn();
        setIsEditMode(false);
      })
      .catch(err => {
        message.error('保存失败');
      });
  };

  // 取消 - 恢复到初始数据
  const handleCancel = () => {
    setAreaList(initialData);
    setIsEditMode(false);
  };

  // 返回列表
  const handleBack = () => {
    navigate('/pipeline/pressure/monitoring');
  };

  // 数据点表格列
  const dataPointColumns = [
    {
      title: '监测区域名称',
      dataIndex: 'areaInfo',
      ellipsis: true,
      width: 450,
      render: (_: unknown, record: MonitorAreaItem) => (
        <TreeSelect
          style={{ width: 400 }}
          placeholder="请选择"
          treeData={option}
          showSearch
          allowClear
          maxTagCount={3}
          treeDefaultExpandAll
          multiple
          disabled={!isEditMode}
          value={(record.areaInfo?.map(area => area.id).filter(id => id != null) as number[]) || []}
          onChange={(checkedIds: number[]) => {
            const newAreaInfo = checkedIds.map((id: number) => ({
              id,
              zoneName: findNodeById(option, id)?.title || '',
            }));
            const newAreaList = areaList.map(item =>
              item.id === record.id ? { ...item, areaInfo: newAreaInfo } : item
            );
            setAreaList(newAreaList);
          }}
          filterTreeNode={(search, node) => {
            const searchText = (node.title ?? '') as string;
            return searchText.toLowerCase().includes(search.toLowerCase());
          }}
        />
      ),
    },
    {
      title: '监测区域压力设备',
      dataIndex: 'devices',
      ellipsis: true,
      render: (_: unknown, record: MonitorAreaItem) =>
        (record.devices ?? []).map((device: any) => device?.name).join('、'),
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      render: (_: unknown, record: any) => (
        <Space size="small">
          <Button type="link" onClick={() => handleConfigPressureDevice(record.id!)} disabled={!isEditMode}>
            配置压力设备
          </Button>
          <Button type="link" danger onClick={() => handleDeleteArea(record.id!)} disabled={!isEditMode}>
            删除
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Wrapper routes={[...(breadcrumbRoutes?.routes || []), { name: '配置' }]} className={styles.wrapper}>
      <FormContent narrow title="管道压力监测配置">
        <div className={styles.headerActions}>
          <Space size="small">
            {isEditMode && (
              <>
                <Button type="primary" icon={<PlusOutlined />} onClick={handleAddArea}>
                  新增监测区域
                </Button>
                <Button
                  type="primary"
                  onClick={() => {
                    setIsDataPointModalVisible(true);
                  }}
                >
                  配置压力数据点
                </Button>
              </>
            )}
          </Space>
        </div>

        <Table rowKey="id" columns={dataPointColumns} dataSource={areaList} pagination={false} scroll={{ x: 800 }} />

        <div className={styles.footerActions}>
          <Space>
            {isEditMode ? (
              <>
                <Button type="primary" onClick={handleSave}>
                  保存
                </Button>
                <Button onClick={handleCancel}>取消</Button>
              </>
            ) : (
              <>
                <Button onClick={handleBack}>返回</Button>
                <Button type="primary" onClick={handleEdit}>
                  编辑
                </Button>
              </>
            )}
          </Space>
        </div>

        {/* 配置压力设备 */}
        <DeviceConfig
          visible={isDeviceConfigModalVisible}
          onCancel={() => setIsDeviceConfigModalVisible(false)}
          value={
            currentAreaId !== null
              ? areaList
                  .find(item => item.id === currentAreaId)
                  ?.devices.map(d => ({
                    deviceId: d.id,
                    name: d.name,
                  }))
              : []
          }
          onConfirm={selectedDevices => {
            if (currentAreaId !== null) {
              const newDevices = selectedDevices
                .filter(s => s.deviceId != null && s.name != null)
                .map(s => ({
                  id: s.deviceId!,
                  name: s.name!,
                  code: s.code ?? '',
                }));

              const newAreaList = areaList.map(item =>
                item.id === currentAreaId
                  ? {
                      ...item,
                      devices: newDevices,
                    }
                  : item
              );
              setAreaList(newAreaList);

              // 更新所有区域的设备 ID 列表（去重）
              const allDeviceIds = newAreaList.flatMap(item => item.devices?.filter(d => d.id).map(d => d.id!) || []);
              setDeviceIds([...new Set(allDeviceIds)]);
            }
          }}
        />
        <DataPointConfig
          visible={isDataPointModalVisible}
          onCancel={() => setIsDataPointModalVisible(false)}
          deviceIds={deviceIds}
        />
      </FormContent>
    </Wrapper>
  );
};

export default PipelinePressureConfig;
